import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../components/Toast';
import { supabase } from '../../supabase';
import { APP_ROUTES } from '../../utils/auth';
import {
  getStoredProfile,
  getStoredProfiles,
  setStoredProfile,
  setStoredProfiles,
} from '../../utils/session';
import { subscribePush } from '../../utils/webpush';
import { DELIVERY_COLUMNS, runActiveDeliveriesQuery } from '../../services/deliveries';
import {
  isActiveMerchantDelivery,
  removeById,
  upsertById,
} from './merchantUtils';

function getLegacyMerchantPhoneCookie() {
  const match = document.cookie.split(';').find((cookie) => cookie.trim().startsWith('wakaana_profile_phone='));
  return match ? decodeURIComponent(match.trim().split('=')[1]) : null;
}

export function useMerchantRuntime({ applyMerchantSheetsConfig } = {}) {
  const navigate = useNavigate();
  const toast = useToast();
  const [profile, setProfile] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [bilanDeliveries, setBilanDeliveries] = useState([]);
  const [products, setProducts] = useState([]);
  const [allProfiles, setAllProfiles] = useState(() => getStoredProfiles());
  const [loading, setLoading] = useState(true);

  const fetchProducts = useCallback(async (storeName) => {
    const { data, error } = await supabase.from('products').select('*').eq('store_name', storeName).order('name');
    if (error) {
      console.error('fetchProducts error:', error.message);
      return;
    }
    if (data) setProducts(data);
  }, []);

  const fetchDeliveries = useCallback(async (storeName) => {
    setLoading(true);
    const [{ data: activeData, error: activeError }, { data: bilanData, error: bilanError }] = await Promise.all([
      runActiveDeliveriesQuery((activeFilter) =>
        supabase
          .from('deliveries')
          .select(DELIVERY_COLUMNS)
          .eq('store', storeName)
          .or(activeFilter)
          .not('expedition', 'eq', true)
          .order('created_at', { ascending: false })
      ),
      supabase
        .from('deliveries')
        .select(DELIVERY_COLUMNS)
        .eq('store', storeName)
        .order('created_at', { ascending: false }),
    ]);
    if (activeData) setDeliveries(activeData);
    if (bilanData) setBilanDeliveries(bilanData);
    if (activeError || bilanError) {
      console.error('fetchDeliveries error:', activeError?.message || bilanError?.message);
      toast('Erreur lors du chargement des commandes', 'error');
    }
    setLoading(false);
  }, [toast]);

  const refreshProfilesForPhone = useCallback(async (phone, currentProfileId) => {
    if (!phone) return [];
    const { data } = await supabase.from('profiles').select('*').eq('phone', phone);
    if (data && data.length > 0) {
      setAllProfiles(data);
      setStoredProfiles(data);
      const merchant = data.find((item) => item.id === currentProfileId);
      if (merchant?.sheets_config && applyMerchantSheetsConfig) {
        applyMerchantSheetsConfig(merchant.sheets_config);
      }
    }
    return data || [];
  }, [applyMerchantSheetsConfig]);

  useEffect(() => {
    let isMounted = true;
    let channel = null;
    let refreshTimer = null;

    const scheduleRefresh = (storeName) => {
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        if (isMounted) fetchDeliveries(storeName);
      }, 900);
    };

    const applyRealtimeChange = (payload, storeName) => {
      const { eventType, new: newRow, old: oldRow } = payload;

      if (eventType === 'DELETE') {
        const deletedId = oldRow?.id;
        if (deletedId) {
          setDeliveries((prev) => removeById(prev, deletedId));
          setBilanDeliveries((prev) => removeById(prev, deletedId));
        }
        return;
      }

      if (!newRow?.id) return;

      setBilanDeliveries((prev) => upsertById(prev, newRow));
      setDeliveries((prev) => {
        const withoutCurrent = removeById(prev, newRow.id);
        if (!isActiveMerchantDelivery(newRow)) return withoutCurrent;
        return upsertById(withoutCurrent, newRow);
      });

      scheduleRefresh(storeName);
    };

    const init = async () => {
      let currentProfile = null;

      const storedProfile = getStoredProfile();
      if (storedProfile?.role === 'merchant') currentProfile = storedProfile;

      if (!currentProfile) {
        const phone = getLegacyMerchantPhoneCookie();
        if (phone) {
          const { data } = await supabase.from('profiles').select('*').eq('phone', phone);
          if (data && data.length > 0) {
            currentProfile = data.find((item) => item.role === 'merchant') || null;
            if (currentProfile) {
              setStoredProfile(currentProfile);
              setStoredProfiles(data);
            }
          }
        }
      }

      if (!currentProfile) {
        navigate(APP_ROUTES.home, { replace: true });
        return;
      }
      if (!isMounted) return;

      setProfile(currentProfile);
      subscribePush(supabase, { role: 'merchant', store: currentProfile.store_name });
      fetchDeliveries(currentProfile.store_name);
      fetchProducts(currentProfile.store_name);
      refreshProfilesForPhone(currentProfile.phone, currentProfile.id);

      channel = supabase
        .channel('realtime:merchant-deliveries')
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'deliveries', filter: `store=eq.${currentProfile.store_name}` },
          (payload) => applyRealtimeChange(payload, currentProfile.store_name)
        )
        .subscribe();
    };

    init();

    return () => {
      isMounted = false;
      if (refreshTimer) clearTimeout(refreshTimer);
      if (channel) supabase.removeChannel(channel);
    };
  }, [fetchDeliveries, fetchProducts, navigate, refreshProfilesForPhone]);

  return {
    profile,
    deliveries,
    bilanDeliveries,
    products,
    allProfiles,
    loading,
    setDeliveries,
    setProducts,
    setAllProfiles,
    fetchDeliveries,
    fetchProducts,
    refreshProfilesForPhone,
  };
}
